import { Add as AddIcon, Remove as RemoveIcon, RestartAlt as ResetIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { BaseWellCurvesResponse } from '@/api/scenarioLayers';
import { computeLayerProductionFromSchedule } from '@/utils/drillingPlanMath';
import {
  convertProductionRatesForDisplay,
  getCumulativeOilAxisLabel,
  getProductionRateAxisLabel,
  SCENARIO_UNIT_BARRELS,
  SCENARIO_UNIT_TONS,
} from '@/utils/scenarioUnits';
import {
  computeInjectionWellsFromRatio,
  DEFAULT_WELL_TYPE_RATIO,
  normalizeWellTypeRatio,
  parseRatioPartInput,
  sanitizeRatioPartInput,
  type WellTypeRatio,
} from '@/utils/wellInjectionRatio';

/** Horizontal rate limit line on oil/liquid axis. */
const RATE_LIMIT_LINE_COLOR = '#D32F2F';

function parseRateLimitInput(raw: string): number | null {
  const t = raw.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function getRateLimitUnitSuffix(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'bbl' : 't';
}

/** Maximum years in the interactive well schedule (convolution input length). */
export const MAX_DRILLING_SCHEDULE_YEARS = 30;
/** Initial adjustable bars: 14 years + control column (+/−). */
const INITIAL_BAR_COUNT = 14;
/** Production chart always shows at most this many years on the X axis (does not grow when adding schedule years). */
const PRODUCTION_CHART_DISPLAY_YEARS = 30;

/** Match layer production line colors (WC and cumulative share right-side axes). */
const WC_AXIS_COLOR = '#C62828';
const CUMULATIVE_OIL_AXIS_COLOR = '#6A1B9A';
/** Axis title text (custom Y labels + X “Year”) on the production chart. */
const CHART_AXIS_TITLE_FONT_PX = 13;

/** Fixed width for oil-rate tick numerals inside the left Y-axis strip (between label and plot). */
const OIL_TICK_BAND_PX = 44;
/** Min width for “100%”-style ticks on the water-cut axis (between WC line and label). */
const WC_TICK_BAND_MIN_PX = 24;
/** Width for cumulative tick numerals (between cumulative line and label). */
const CUM_TICK_BAND_PX = 62;

/**
 * Primary controls for axis lines & labels vs the **plot** (the area where series are drawn).
 * All distances are in px along the horizontal axis.
 *
 * 1. **plotLeftToOilLiquidLabelPx** — from the **left edge of the plot** to the oil/liquid label anchor (label is left of the plot).
 * 2. **wcAxisLineToWcLabelPx** — from the **water-cut axis line** (coincides with the **right edge of the plot**) to the WC label anchor (includes tick column + gap to text).
 * 3. **plotRightToCumulativeAxisLinePx** — from the **right edge of the plot** to the **cumulative oil axis line** (WC strip + empty gap).
 * 4. **plotRightToCumulativeLabelPx** — from the **right edge of the plot** to the cumulative label anchor.
 */
export const CHART_AXIS_INSETS = {
  plotLeftToOilLiquidLabelPx: 48,
  wcAxisLineToWcLabelPx: 37,
  plotRightToCumulativeAxisLinePx: 72,
  plotRightToCumulativeLabelPx: 109,
} as const;

type AxisBlockLayout = {
  gapBetweenRightBlocksPx: number;
  leftRate: { lineToTickPx: number; tickBandPx: number; titleOutwardPx: number };
  rightWaterCut: { lineToTickPx: number; tickBandPx: number; titleOutwardPx: number };
  rightCumulative: { lineToTickPx: number; tickBandPx: number; titleOutwardPx: number };
};

function buildAxisBlockLayout(insets: typeof CHART_AXIS_INSETS): AxisBlockLayout {
  const leftTitleOutward = Math.max(4, insets.plotLeftToOilLiquidLabelPx - OIL_TICK_BAND_PX);

  const wcTickBand = Math.min(
    40,
    Math.max(WC_TICK_BAND_MIN_PX, insets.wcAxisLineToWcLabelPx - 4),
  );
  const wcTitleOutward = Math.max(4, insets.wcAxisLineToWcLabelPx - wcTickBand);

  const gapBetweenRightBlocksPx = Math.max(0, insets.plotRightToCumulativeAxisLinePx - wcTickBand);

  const cumTitleOutward = Math.max(
    4,
    insets.plotRightToCumulativeLabelPx - insets.plotRightToCumulativeAxisLinePx - CUM_TICK_BAND_PX,
  );

  return {
    gapBetweenRightBlocksPx,
    leftRate: {
      lineToTickPx: 6,
      tickBandPx: OIL_TICK_BAND_PX,
      titleOutwardPx: leftTitleOutward,
    },
    rightWaterCut: {
      lineToTickPx: 6,
      tickBandPx: wcTickBand,
      titleOutwardPx: wcTitleOutward,
    },
    rightCumulative: {
      lineToTickPx: 10,
      tickBandPx: CUM_TICK_BAND_PX,
      titleOutwardPx: cumTitleOutward,
    },
  };
}

/** Derived layout for Recharts (from {@link CHART_AXIS_INSETS}). */
const Y_AXIS_BLOCKS = buildAxisBlockLayout(CHART_AXIS_INSETS);

/** Left axis strip: line + tick numerals only; title is custom, to the left of this band (see margin.left). */
const YAXIS_LEFT_W = Y_AXIS_BLOCKS.leftRate.tickBandPx;
/** WC block width only (gap is a separate Y-axis so WC title does not shift). */
const YAXIS_RIGHT_WC_W = Y_AXIS_BLOCKS.rightWaterCut.tickBandPx;
const YAXIS_RIGHT_CUM_W = Y_AXIS_BLOCKS.rightCumulative.tickBandPx;

/**
 * Recharts adds every right Y-axis `width` to offset — do not duplicate axis widths here.
 * Only reserve extra room for custom labels past the outermost axis.
 */
const PRODUCTION_CHART_MARGIN = {
  top: 8,
  right:
    Math.max(
      Y_AXIS_BLOCKS.rightWaterCut.titleOutwardPx,
      Y_AXIS_BLOCKS.rightCumulative.titleOutwardPx,
    ) + 32,
  /** Room for the left Y-axis label (custom; placed left of the axis viewBox). */
  left: Y_AXIS_BLOCKS.leftRate.titleOutwardPx + 10,
  bottom: 26,
} as const;

const MIN_START_YEAR = 1900;
const MAX_START_YEAR = 2100;

/** Props passed to Recharts `label.content` for Cartesian Y axes */
type YAxisLabelContentProps = {
  viewBox?: { x: number; y: number; width: number; height: number };
};

function createLeftRateAxisLabel(title: string) {
  return function YAxisLabelLeftOilLiquid(props: YAxisLabelContentProps) {
    const { viewBox } = props;
    if (viewBox == null || viewBox.height <= 0) return null;
    const d = Y_AXIS_BLOCKS.leftRate.titleOutwardPx;
    const x = viewBox.x - d;
    const y = viewBox.y + viewBox.height / 2;
    return (
      <text
        x={x}
        y={y}
        transform={`rotate(-90, ${x}, ${y})`}
        textAnchor="middle"
        fill="#666"
        fontSize={CHART_AXIS_TITLE_FONT_PX}
      >
        {title}
      </text>
    );
  };
}

function createCumulativeOilAxisLabel(title: string) {
  return function YAxisLabelCumulativeOil(props: YAxisLabelContentProps) {
    const { viewBox } = props;
    if (viewBox == null || viewBox.height <= 0) return null;
    const d = Y_AXIS_BLOCKS.rightCumulative.titleOutwardPx;
    const x = viewBox.x + viewBox.width + d;
    const y = viewBox.y + viewBox.height / 2;
    return (
      <text
        x={x}
        y={y}
        transform={`rotate(90, ${x}, ${y})`}
        textAnchor="middle"
        fill={CUMULATIVE_OIL_AXIS_COLOR}
        fontSize={CHART_AXIS_TITLE_FONT_PX}
      >
        {title}
      </text>
    );
  };
}

function YAxisLabelWaterCut(props: YAxisLabelContentProps) {
  const { viewBox } = props;
  if (viewBox == null || viewBox.height <= 0) return null;
  const d = Y_AXIS_BLOCKS.rightWaterCut.titleOutwardPx;
  const x = viewBox.x + viewBox.width + d;
  const y = viewBox.y + viewBox.height / 2;
  return (
    <text
      x={x}
      y={y}
      transform={`rotate(90, ${x}, ${y})`}
      textAnchor="middle"
      fill={WC_AXIS_COLOR}
      fontSize={CHART_AXIS_TITLE_FONT_PX}
    >
      Water cut %
    </text>
  );
}

function clampStartYear(y: number): number {
  if (!Number.isFinite(y)) return new Date().getFullYear();
  return Math.min(MAX_START_YEAR, Math.max(MIN_START_YEAR, Math.round(y)));
}

/** Round axis max up to 1 / 2 / 2.5 / 5 × 10^n so top ticks stay readable. */
function niceCeil(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const exp = Math.floor(Math.log10(value));
  const pow = 10 ** exp;
  const frac = value / pow;
  let niceFrac: number;
  if (frac <= 1) niceFrac = 1;
  else if (frac <= 2) niceFrac = 2;
  else if (frac <= 2.5) niceFrac = 2.5;
  else if (frac <= 5) niceFrac = 5;
  else niceFrac = 10;
  return niceFrac * pow;
}

function formatRateAxisTick(value: number): string {
  if (!Number.isFinite(value)) return '';
  const abs = Math.abs(value);
  if (abs === 0) return '0';
  if (abs >= 1_000_000) {
    const scaled = value / 1_000_000;
    return `${scaled >= 10 ? scaled.toFixed(0) : scaled.toFixed(1)}M`;
  }
  if (abs >= 10_000) {
    const scaled = value / 1_000;
    return `${scaled >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)}k`;
  }
  if (abs >= 100) return String(Math.round(value));
  if (abs >= 10) return value.toFixed(1);
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2);
}

const START_YEAR_INPUT_MAX_LEN = 4;

function sanitizeStartYearInput(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, START_YEAR_INPUT_MAX_LEN);
}

/** Fixed column for +/- so bar count does not squeeze controls horizontally. */
const WELL_BAR_CONTROLS_COL_WIDTH_PX = 48;
/** Vertical gap between + and −; each button gets (trackHeight − gap) / 2. */
const WELL_BAR_CONTROLS_BUTTON_GAP = 0.75;

/** Horizontal padding (px) inside the bar column for the year caption (left + right). */
const BAR_YEAR_LABEL_INSET_PX = 4;
const BAR_YEAR_LABEL_MIN_FONT_PX = 5;
const BAR_YEAR_LABEL_MAX_FONT_PX = 11;

function measureBarYearTextWidth(
  text: string,
  fontSizePx: number,
  fontFamily: string,
  fontWeight: string | number,
): number {
  if (typeof document === 'undefined') return 0;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx == null) return 0;
  ctx.font = `${fontWeight} ${fontSizePx}px ${fontFamily}`;
  return ctx.measureText(text).width;
}

function fitBarYearCaption(
  innerWidthPx: number,
  year: number,
  fontFamily: string,
  fontWeight: string | number,
): { text: string; fontSize: number } {
  const w = Math.max(0, innerWidthPx);
  const candidates = [String(year), String(year % 100).padStart(2, '0'), String(year % 10)];
  for (const text of candidates) {
    for (let fs = BAR_YEAR_LABEL_MAX_FONT_PX; fs >= BAR_YEAR_LABEL_MIN_FONT_PX; fs -= 1) {
      if (measureBarYearTextWidth(text, fs, fontFamily, fontWeight) <= w) {
        return { text, fontSize: fs };
      }
    }
  }
  return { text: String(year % 10), fontSize: BAR_YEAR_LABEL_MIN_FONT_PX };
}

function BarColumnYearLabel({ year }: { year: number }) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [caption, setCaption] = useState<{ text: string; fontSize: number }>(() => ({
    text: String(year),
    fontSize: BAR_YEAR_LABEL_MAX_FONT_PX,
  }));

  const fontFamily =
    typeof theme.typography.fontFamily === 'string'
      ? theme.typography.fontFamily
      : 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif';
  const fontWeight = theme.typography.caption.fontWeight ?? 400;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (el == null) return;

    const apply = () => {
      const inner = Math.max(0, el.clientWidth - BAR_YEAR_LABEL_INSET_PX);
      setCaption(fitBarYearCaption(inner, year, fontFamily, String(fontWeight)));
    };

    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [year, fontFamily, fontWeight]);

  return (
    <Box
      ref={containerRef}
      title={`${year}`}
      sx={{
        mt: 0.25,
        lineHeight: 1.05,
        minHeight: 14,
        width: '100%',
        minWidth: 0,
        pl: '2px',
        pr: '2px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Typography
        component="span"
        variant="caption"
        sx={{
          fontSize: caption.fontSize,
          lineHeight: 1.05,
          whiteSpace: 'nowrap',
          textAlign: 'center',
        }}
      >
        {caption.text}
      </Typography>
    </Box>
  );
}

export interface LayerDrillingPlanPanelProps {
  layerName: string;
  /** Passed from parent after batch fetch (interactive tab only) */
  curves: BaseWellCurvesResponse | null;
  curvesError: string | null;
  plan: {
    startYear: number;
    wellsPerYear: number[];
    injectionWellsPerYear: number[];
    injectionRatio?: WellTypeRatio;
    injectionRatioLinked?: boolean;
  };
  onPlanChange: (plan: {
    startYear: number;
    wellsPerYear: number[];
    injectionWellsPerYear: number[];
    injectionRatio?: WellTypeRatio;
    injectionRatioLinked?: boolean;
  }) => void;
  /** scenarios.unit_id: 1 = tons (as-is), 2 = barrels (convert for display) */
  unitId?: number;
}

const PRODUCTION_BAR_COLOR = 'primary.main';
const INJECTION_BAR_COLOR = '#00838F';

function useWellsBarDrag(
  maxScale: number,
  setValue: (index: number, value: number) => void,
) {
  return useCallback(
    (index: number) => (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault();
      const track = e.currentTarget;

      const updateFromEvent = (ev: PointerEvent) => {
        const r = track.getBoundingClientRect();
        const frac = Math.min(1, Math.max(0, (r.bottom - ev.clientY) / r.height));
        const v = Math.round(frac * maxScale);
        setValue(index, Math.max(0, Math.min(maxScale, v)));
      };

      updateFromEvent(e.nativeEvent);

      const move = (ev: PointerEvent) => updateFromEvent(ev);
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    },
    [maxScale, setValue],
  );
}

type WellScheduleBarRowProps = {
  values: number[];
  startYear: number;
  barColor: string;
  maxBarScale: number;
  onBarChange: (index: number, value: number) => void;
  showControls?: boolean;
  onAddYear?: () => void;
  onRemoveYear?: () => void;
  canAddYear?: boolean;
  canRemoveYear?: boolean;
  reserveControlColumn?: boolean;
  readOnly?: boolean;
};

type WellInjectionRatioFormProps = {
  ratio: WellTypeRatio;
  linked: boolean;
  onRatioChange: (ratio: WellTypeRatio) => void;
  onLinkedChange: (linked: boolean) => void;
  onApply: () => void;
};

function WellInjectionRatioForm({
  ratio,
  linked,
  onRatioChange,
  onLinkedChange,
  onApply,
}: WellInjectionRatioFormProps) {
  const normalized = normalizeWellTypeRatio(ratio);
  const [injectionInput, setInjectionInput] = useState(() => String(normalized.injection));
  const [productionInput, setProductionInput] = useState(() => String(normalized.production));

  useLayoutEffect(() => {
    setInjectionInput(String(normalized.injection));
    setProductionInput(String(normalized.production));
  }, [normalized.injection, normalized.production]);

  const commitInjectionPart = useCallback(() => {
    const next = parseRatioPartInput(injectionInput, normalized.injection);
    setInjectionInput(String(next));
    onRatioChange({ injection: next, production: normalized.production });
  }, [injectionInput, normalized.injection, normalized.production, onRatioChange]);

  const commitProductionPart = useCallback(() => {
    const next = parseRatioPartInput(productionInput, normalized.production);
    setProductionInput(String(next));
    onRatioChange({ injection: normalized.injection, production: next });
  }, [normalized.injection, normalized.production, productionInput, onRatioChange]);

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
        Injection to production ratio
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <TextField
          label="Injection"
          type="text"
          size="small"
          value={injectionInput}
          onChange={(e) => setInjectionInput(sanitizeRatioPartInput(e.target.value))}
          onBlur={commitInjectionPart}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            }
          }}
          inputProps={{ inputMode: 'numeric', 'aria-label': 'Injection wells in ratio', min: 1 }}
          sx={{ width: 108, flexShrink: 0 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          :
        </Typography>
        <TextField
          label="Production"
          type="text"
          size="small"
          value={productionInput}
          onChange={(e) => setProductionInput(sanitizeRatioPartInput(e.target.value))}
          onBlur={commitProductionPart}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            }
          }}
          inputProps={{ inputMode: 'numeric', 'aria-label': 'Production wells in ratio', min: 1 }}
          sx={{ width: 108, flexShrink: 0 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 200px', minWidth: 0 }}>
          Integer ratio on cumulative wells (e.g. 2:1 = two injection wells total per one production well drilled so far).
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mt: 1.5,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={linked}
              onChange={(_, checked) => onLinkedChange(checked)}
              inputProps={{ 'aria-label': 'Sync injection schedule to ratio' }}
            />
          }
          label="Sync injection schedule to ratio"
        />
        <Button
          type="button"
          size="small"
          variant="outlined"
          disabled={linked}
          onClick={onApply}
        >
          Apply ratio
        </Button>
      </Box>
    </Box>
  );
}

function WellScheduleBarRow({
  values,
  startYear,
  barColor,
  maxBarScale,
  onBarChange,
  showControls = false,
  onAddYear,
  onRemoveYear,
  canAddYear = false,
  canRemoveYear = false,
  reserveControlColumn = false,
  readOnly = false,
}: WellScheduleBarRowProps) {
  const onBarPointerDown = useWellsBarDrag(maxBarScale, onBarChange);

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.25, height: 232, userSelect: 'none' }}>
      {values.map((v, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.2,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: v > 0 ? 'text.primary' : 'text.secondary',
            }}
          >
            {v}
          </Typography>
          <Box
            onPointerDown={readOnly ? undefined : onBarPointerDown(i)}
            sx={{
              flex: 1,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              bgcolor: 'action.hover',
              borderRadius: 0.5,
              cursor: readOnly ? 'default' : 'ns-resize',
              touchAction: readOnly ? 'auto' : 'none',
              border: 1,
              borderColor: 'divider',
              minHeight: 0,
              opacity: readOnly ? 0.88 : 1,
            }}
          >
            <Box
              sx={{
                width: '82%',
                mx: 'auto',
                bgcolor: barColor,
                height: `${(v / maxBarScale) * 100}%`,
                minHeight: v > 0 ? 3 : 0,
                borderRadius: 0.5,
                pointerEvents: 'none',
              }}
            />
          </Box>
          <BarColumnYearLabel year={startYear + i} />
        </Box>
      ))}
      {(showControls || reserveControlColumn) && (
        <Box
          sx={{
            flexShrink: 0,
            width: WELL_BAR_CONTROLS_COL_WIDTH_PX,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ height: 16, flexShrink: 0 }} />
          {showControls ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: WELL_BAR_CONTROLS_BUTTON_GAP,
              }}
            >
              <Button
                type="button"
                variant="outlined"
                color="primary"
                size="small"
                fullWidth
                aria-label="Add year at end of schedule"
                disabled={!canAddYear}
                onClick={onAddYear}
                sx={{
                  flex: '1 1 0',
                  minHeight: 0,
                  py: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AddIcon fontSize="small" />
              </Button>
              <Button
                type="button"
                variant="outlined"
                color="error"
                size="small"
                fullWidth
                aria-label="Remove last year from schedule"
                disabled={!canRemoveYear}
                onClick={onRemoveYear}
                sx={{
                  flex: '1 1 0',
                  minHeight: 0,
                  py: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <RemoveIcon fontSize="small" />
              </Button>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0 }} />
          )}
          <Box sx={{ mt: 0.25, minHeight: 14, flexShrink: 0 }} />
        </Box>
      )}
    </Box>
  );
}

function parseCurvesPayload(data: BaseWellCurvesResponse | null) {
  if (!data) {
    return { baseOil: [] as number[], baseLiquid: [] as number[], hasLiquidModel: false };
  }
  const oil = (data.oil_rate ?? [])
    .map((x) => Number(x))
    .map((x) => (Number.isFinite(x) && x >= 0 ? x : 0));
  const liq = (data.liquid_rate ?? [])
    .map((x) => Number(x))
    .map((x) => (Number.isFinite(x) && x >= 0 ? x : 0));
  const liqOk = liq.length > 0 && liq.length === oil.length;
  return {
    baseOil: oil,
    baseLiquid: liqOk ? liq : [],
    hasLiquidModel: liqOk,
  };
}

export const LayerDrillingPlanPanel: React.FC<LayerDrillingPlanPanelProps> = memo(
  ({ layerName, curves, curvesError, plan, onPlanChange, unitId = SCENARIO_UNIT_TONS }) => {
    const newWells = plan.wellsPerYear;
    const injectionRatio = useMemo(
      () => normalizeWellTypeRatio(plan.injectionRatio ?? DEFAULT_WELL_TYPE_RATIO),
      [plan.injectionRatio],
    );
    const injectionRatioLinked = plan.injectionRatioLinked ?? false;
    const injectionWells = useMemo(() => {
      const raw = plan.injectionWellsPerYear ?? [];
      if (raw.length === newWells.length) {
        return raw;
      }
      if (raw.length > newWells.length) {
        return raw.slice(0, newWells.length);
      }
      return [...raw, ...Array(newWells.length - raw.length).fill(0)];
    }, [newWells, plan.injectionWellsPerYear]);
    const committedStartYear = plan.startYear;
    /** Raw digits while typing — no clamping or coercion until blur. */
    const [startYearInput, setStartYearInput] = useState<string>(() => String(plan.startYear));

    /** Raw input for optional oil/liquid rate limit (display units: t or bbl). */
    const [rateLimitInput, setRateLimitInput] = useState('');

    useLayoutEffect(() => {
      setStartYearInput(String(plan.startYear));
    }, [plan.startYear]);

    const rateLimitValue = useMemo(() => parseRateLimitInput(rateLimitInput), [rateLimitInput]);

    const { baseOil, baseLiquid, hasLiquidModel } = useMemo(() => parseCurvesPayload(curves), [curves]);

    const maxBarScale = useMemo(() => {
      const peak = newWells.length ? Math.max(...newWells) : 0;
      return Math.min(500, Math.max(12, Math.ceil(peak * 1.2), 5));
    }, [newWells]);

    const maxInjectionBarScale = useMemo(() => {
      const peak = injectionWells.length ? Math.max(...injectionWells) : 0;
      return Math.min(500, Math.max(12, Math.ceil(peak * 1.2), 5));
    }, [injectionWells]);

    const emitPlanChange = useCallback(
      (patch: Partial<LayerDrillingPlanPanelProps['plan']>) => {
        onPlanChange({
          ...plan,
          ...patch,
        });
      },
      [onPlanChange, plan],
    );

    const applyRatioToInjection = useCallback(
      (productionPerYear: number[], ratio: WellTypeRatio = injectionRatio) =>
        computeInjectionWellsFromRatio(productionPerYear, ratio),
      [injectionRatio],
    );

    const setWellCount = useCallback((index: number, value: number) => {
      const nextWells = [...newWells];
      nextWells[index] = value;
      const nextInjection = injectionRatioLinked
        ? applyRatioToInjection(nextWells)
        : injectionWells;
      emitPlanChange({
        wellsPerYear: nextWells,
        injectionWellsPerYear: nextInjection,
      });
    }, [applyRatioToInjection, emitPlanChange, injectionRatioLinked, injectionWells, newWells]);

    const setInjectionWellCount = useCallback((index: number, value: number) => {
      if (injectionRatioLinked) {
        return;
      }
      const next = [...injectionWells];
      next[index] = value;
      emitPlanChange({
        injectionWellsPerYear: next,
      });
    }, [emitPlanChange, injectionRatioLinked, injectionWells]);

    const handleInjectionRatioChange = useCallback((ratio: WellTypeRatio) => {
      const normalized = normalizeWellTypeRatio(ratio);
      const nextInjection = injectionRatioLinked
        ? applyRatioToInjection(newWells, normalized)
        : injectionWells;
      emitPlanChange({
        injectionRatio: normalized,
        injectionWellsPerYear: nextInjection,
      });
    }, [applyRatioToInjection, emitPlanChange, injectionRatioLinked, injectionWells, newWells]);

    const handleInjectionRatioLinkedChange = useCallback((linked: boolean) => {
      const nextInjection = linked
        ? applyRatioToInjection(newWells)
        : injectionWells;
      emitPlanChange({
        injectionRatioLinked: linked,
        injectionWellsPerYear: nextInjection,
      });
    }, [applyRatioToInjection, emitPlanChange, injectionWells, newWells]);

    const handleApplyInjectionRatio = useCallback(() => {
      emitPlanChange({
        injectionWellsPerYear: applyRatioToInjection(newWells),
      });
    }, [applyRatioToInjection, emitPlanChange, newWells]);

    const production = useMemo(
      () =>
        computeLayerProductionFromSchedule(
          newWells,
          baseOil,
          hasLiquidModel ? baseLiquid : null,
        ),
      [newWells, baseOil, baseLiquid, hasLiquidModel],
    );

    const productionChartData = useMemo(() => {
      const fullLen = Math.max(production.oil.length, 1);
      const n = Math.min(fullLen, PRODUCTION_CHART_DISPLAY_YEARS);
      let cumOil = 0;
      return Array.from({ length: n }, (_, i) => {
        const rawOil = production.oil[i] ?? 0;
        const rawLiquid = hasLiquidModel ? production.liquid[i] ?? 0 : 0;
        const { oil: o, liquid } = convertProductionRatesForDisplay(unitId, rawOil, rawLiquid);
        cumOil += o;
        return {
          year: committedStartYear + i,
          oil: o,
          liquid: hasLiquidModel ? liquid : 0,
          wc: hasLiquidModel ? production.wcPct[i] ?? 0 : 0,
          cumOil,
        };
      });
    }, [production, hasLiquidModel, committedStartYear, unitId]);

    const rateAxisLabelContent = useMemo(
      () => createLeftRateAxisLabel(getProductionRateAxisLabel(unitId)),
      [unitId],
    );
    const cumulativeAxisLabelContent = useMemo(
      () => createCumulativeOilAxisLabel(getCumulativeOilAxisLabel(unitId)),
      [unitId],
    );

    const rateAxisDomain = useMemo((): [number, number] => {
      let maxVal = 0;
      for (const row of productionChartData) {
        if (Number.isFinite(row.oil)) maxVal = Math.max(maxVal, row.oil);
        if (Number.isFinite(row.liquid)) maxVal = Math.max(maxVal, row.liquid);
      }
      if (rateLimitValue != null && Number.isFinite(rateLimitValue)) {
        maxVal = Math.max(maxVal, rateLimitValue);
      }
      if (maxVal <= 0) return [0, 1];
      return [0, niceCeil(maxVal * 1.05)];
    }, [productionChartData, rateLimitValue]);

    const rateLimitUnitSuffix = getRateLimitUnitSuffix(unitId);

    const commitStartYearFromInput = useCallback(() => {
      const t = startYearInput.trim();
      if (t === '') {
        setStartYearInput(String(committedStartYear));
        return;
      }
      const n = Number.parseInt(t, 10);
      if (!Number.isFinite(n)) {
        setStartYearInput(String(committedStartYear));
        return;
      }
      const y = clampStartYear(n);
      emitPlanChange({
        startYear: y,
      });
      setStartYearInput(String(y));
    }, [startYearInput, committedStartYear, emitPlanChange]);

    const handleReset = useCallback(() => {
      emitPlanChange({
        wellsPerYear: Array(INITIAL_BAR_COUNT).fill(0),
        injectionWellsPerYear: Array(INITIAL_BAR_COUNT).fill(0),
      });
    }, [emitPlanChange]);

    const handleAddScheduleYear = useCallback(() => {
      if (newWells.length >= MAX_DRILLING_SCHEDULE_YEARS) return;
      const nextWells = [...newWells, 0];
      const nextInjection = injectionRatioLinked
        ? applyRatioToInjection(nextWells)
        : [...injectionWells, 0];
      emitPlanChange({
        wellsPerYear: nextWells,
        injectionWellsPerYear: nextInjection,
      });
    }, [applyRatioToInjection, emitPlanChange, injectionRatioLinked, injectionWells, newWells]);

    const handleRemoveScheduleYear = useCallback(() => {
      if (newWells.length <= INITIAL_BAR_COUNT) return;
      const nextWells = newWells.slice(0, -1);
      const nextInjection = injectionRatioLinked
        ? applyRatioToInjection(nextWells)
        : injectionWells.slice(0, -1);
      emitPlanChange({
        wellsPerYear: nextWells,
        injectionWellsPerYear: nextInjection,
      });
    }, [applyRatioToInjection, emitPlanChange, injectionRatioLinked, injectionWells, newWells]);

    if (curvesError) {
      return (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {layerName}
            </Typography>
            <Alert severity="error">{curvesError}</Alert>
          </CardContent>
        </Card>
      );
    }

    if (!baseOil.length) {
      return (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {layerName}
            </Typography>
            <Alert severity="warning">No oil decline curve returned for this layer.</Alert>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            {layerName}
          </Typography>

          {!hasLiquidModel && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Liquid / WC model is not available for this layer; only oil rate is shown. Convolution for liquid requires
              liquid_rate from the server.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Layer production
          </Typography>
          <Box sx={{ mb: 1 }}>
            <TextField
              label={`Rate limit (${rateLimitUnitSuffix})`}
              type="text"
              size="small"
              value={rateLimitInput}
              onChange={(e) => setRateLimitInput(e.target.value.replace(/[^\d.,]/g, ''))}
              placeholder="Optional"
              inputProps={{ inputMode: 'decimal', 'aria-label': `Rate limit in ${rateLimitUnitSuffix}` }}
              sx={{ width: 180 }}
            />
          </Box>
          <Box
            sx={(theme) => ({
              height: 300,
              minWidth: 0,
              // Bleed right only: left edge matches CardContent (same as well bars); extra width on the right.
              mr: `-${theme.spacing(2)}`,
              width: `calc(100% + ${theme.spacing(2)})`,
            })}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={productionChartData} margin={PRODUCTION_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 10 }}
                  angle={-35}
                  textAnchor="end"
                  height={52}
                  interval="preserveStartEnd"
                  tickMargin={4}
                  label={{
                    value: 'Year',
                    position: 'insideBottom',
                    offset: 0,
                    style: { fontSize: CHART_AXIS_TITLE_FONT_PX },
                  }}
                />
                <YAxis
                  yAxisId="rate"
                  width={YAXIS_LEFT_W}
                  domain={rateAxisDomain}
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatRateAxisTick}
                  tickMargin={Y_AXIS_BLOCKS.leftRate.lineToTickPx}
                  label={{ content: rateAxisLabelContent }}
                  allowDecimals
                />
                <YAxis
                  yAxisId="axis_waterCut"
                  orientation="right"
                  domain={[0, 100]}
                  stroke={WC_AXIS_COLOR}
                  tick={{ fontSize: 10, fill: WC_AXIS_COLOR }}
                  tickFormatter={(x) => `${x}%`}
                  tickMargin={Y_AXIS_BLOCKS.rightWaterCut.lineToTickPx}
                  width={YAXIS_RIGHT_WC_W}
                  label={{ content: YAxisLabelWaterCut }}
                />
                {/* Layout-only band between WC and cumulative (do not use hide — hidden axes get no layout width). */}
                {Y_AXIS_BLOCKS.gapBetweenRightBlocksPx > 0 ? (
                  <YAxis
                    yAxisId="axis_waterCutGap"
                    orientation="right"
                    width={Y_AXIS_BLOCKS.gapBetweenRightBlocksPx}
                    domain={[0, 1]}
                    type="number"
                    tick={false}
                    axisLine={false}
                  />
                ) : null}
                <YAxis
                  yAxisId="z_cumulativeOil"
                  orientation="right"
                  stroke={CUMULATIVE_OIL_AXIS_COLOR}
                  tick={{ fontSize: 10, fill: CUMULATIVE_OIL_AXIS_COLOR }}
                  tickMargin={Y_AXIS_BLOCKS.rightCumulative.lineToTickPx}
                  width={YAXIS_RIGHT_CUM_W}
                  label={{ content: cumulativeAxisLabelContent }}
                />
                <RechartsTooltip />
                <Legend
                  wrapperStyle={{ paddingLeft: 0, marginLeft: 0, paddingRight: 0 }}
                  iconType="line"
                />
                <Line yAxisId="rate" type="monotone" dataKey="oil" name="Oil" stroke="#5D4037" dot={false} strokeWidth={2} />
                {rateLimitValue != null && (
                  <ReferenceLine
                    yAxisId="rate"
                    y={rateLimitValue}
                    stroke={RATE_LIMIT_LINE_COLOR}
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    ifOverflow="extendDomain"
                    label={{
                      value: `${rateLimitValue} ${rateLimitUnitSuffix}`,
                      position: 'insideTopRight',
                      fill: RATE_LIMIT_LINE_COLOR,
                      fontSize: 11,
                    }}
                  />
                )}
                {hasLiquidModel && (
                  <Line
                    yAxisId="rate"
                    type="monotone"
                    dataKey="liquid"
                    name="Liquid"
                    stroke="#1565C0"
                    dot={false}
                    strokeWidth={2}
                  />
                )}
                {hasLiquidModel && (
                  <Line
                    yAxisId="axis_waterCut"
                    type="monotone"
                    dataKey="wc"
                    name="Water cut"
                    stroke={WC_AXIS_COLOR}
                    dot={false}
                    strokeWidth={2}
                  />
                )}
                <Line
                  yAxisId="z_cumulativeOil"
                  type="monotone"
                  dataKey="cumOil"
                  name="Cumulative oil"
                  stroke={CUMULATIVE_OIL_AXIS_COLOR}
                  dot={false}
                  strokeWidth={2}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              mt: 2,
              mb: 1,
            }}
          >
            <TextField
              label="Start year"
              type="text"
              size="small"
              value={startYearInput}
              onChange={(e) => setStartYearInput(sanitizeStartYearInput(e.target.value))}
              onBlur={commitStartYearFromInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              inputProps={{ inputMode: 'numeric', 'aria-label': 'Start year' }}
              sx={{ width: 180, flexShrink: 0 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 220px', minWidth: 0 }}>
              Drag a bar vertically to change new production wells for that year.
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Production wells
          </Typography>
          <WellScheduleBarRow
            values={newWells}
            startYear={committedStartYear}
            barColor={PRODUCTION_BAR_COLOR}
            maxBarScale={maxBarScale}
            onBarChange={setWellCount}
            showControls
            onAddYear={handleAddScheduleYear}
            onRemoveYear={handleRemoveScheduleYear}
            canAddYear={newWells.length < MAX_DRILLING_SCHEDULE_YEARS}
            canRemoveYear={newWells.length > INITIAL_BAR_COUNT}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
            Injection wells
          </Typography>
          <WellInjectionRatioForm
            ratio={injectionRatio}
            linked={injectionRatioLinked}
            onRatioChange={handleInjectionRatioChange}
            onLinkedChange={handleInjectionRatioLinkedChange}
            onApply={handleApplyInjectionRatio}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {injectionRatioLinked
              ? 'Injection bars are recalculated from cumulative production wells to keep the ratio.'
              : 'Drag a bar vertically to change new injection wells for that year, or apply the cumulative ratio above.'}
          </Typography>
          <WellScheduleBarRow
            values={injectionWells}
            startYear={committedStartYear}
            barColor={INJECTION_BAR_COLOR}
            maxBarScale={maxInjectionBarScale}
            onBarChange={setInjectionWellCount}
            reserveControlColumn
            readOnly={injectionRatioLinked}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button size="small" startIcon={<ResetIcon />} onClick={handleReset}>
              Reset to zero
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  },
);

LayerDrillingPlanPanel.displayName = 'LayerDrillingPlanPanel';
